import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SelectableCard } from '../components/onboarding/SelectableCard';
import { SetupScaffold } from '../components/onboarding/SetupScaffold';
import { SETUP_CONTENT_TYPES, SETUP_CONTENT_KEY } from '../components/onboarding/accountSetup';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import { crearEmpresaDesdeCuestionario } from '../lib/altaDesdeCuestionario';
import { usePermissions } from '../context/PermissionContext';
import { Loader2 } from 'lucide-react';

/**
 * Cuestionario post-registro, paso 3: tipo de contenido a crear.
 * Réplica del frame de Figma (node 105:687) reutilizando tokens del DS v3.
 *
 * - **Multi-select para todos los roles** (a diferencia de los pasos 1 y 2): se
 *   pueden elegir 1 o más de los 3 tipos de contenido. Las cards acá son
 *   **horizontales** (imagen 112×112 a la izquierda, label a la derecha) →
 *   `SelectableCard` con `orientation="horizontal"` (ADR-714).
 * - "Siguiente" queda deshabilitado hasta elegir al menos una opción; al continuar
 *   guarda la selección (array JSON) y va al paso 4 (`/configuracion-cuenta/producto`).
 * - Mobile: pantalla completa; desktop (`lg:`): columna centrada acotada.
 */
export const AccountSetupContent = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [selected, setSelected] = useState<string[]>([]);
  const [creando, setCreando] = useState(false);
  const creandoRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Fin del cuestionario: acá se CREA LA EMPRESA. El paso 4 (producto) ya necesita
  // `id_company` y créditos para generar de verdad, y dejarlo para el final haría que
  // quien abandona a mitad no tenga dónde imputar la generación.
  // Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real.
  const handleNext = async () => {
    // El guard es un REF, no el estado: `setCreando(true)` no se ve hasta el próximo
    // render, así que dos toques rápidos entrarían los dos con `creando === false` y
    // dispararían dos altas. El ref se actualiza en el acto.
    // Ver ADR-2026-08-01-alta-de-empresa-sin-carrera.
    if (selected.length === 0 || creandoRef.current) return;
    creandoRef.current = true;
    try {
      localStorage.setItem(SETUP_CONTENT_KEY, JSON.stringify(selected));
    } catch {}

    setCreando(true);
    setError(null);
    const res = await crearEmpresaDesdeCuestionario();
    if (!res.ok) {
      // Sin empresa no se puede seguir: el usuario se queda en el paso 3 y reintenta.
      setError(res.error ?? 'No pudimos crear tu cuenta. Probá de nuevo.');
      setCreando(false);
      creandoRef.current = false; // habilita el reintento
      return;
    }
    // Los permisos se calculan a partir de la empresa: sin refrescar, el usuario
    // entraría al paso 4 con los permisos de "sin empresa".
    await refreshPermissions();
    navigate(ONBOARDING_V2.setupProducto);
  };

  return (
    <SetupScaffold
      footer={
        <button
          type="button"
          onClick={handleNext}
          disabled={selected.length === 0 || creando}
          className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] disabled:cursor-not-allowed disabled:bg-[#9F9FA9] md:w-auto md:px-10"
        >
          {creando && <Loader2 className="size-4 animate-spin" />}
          {creando ? 'Preparando tu cuenta…' : 'Siguiente'}
        </button>
      }
    >
      <h1 className="text-ds-heading-xl text-[#18181B] md:text-center">
        ¿Qué tipo de contenido estás buscando crear?
      </h1>

      {/* El alta de la empresa ocurre acá: si falla, el usuario se queda en el paso
          con el motivo a la vista y puede reintentar sin perder lo elegido. */}
      {error && (
        <div
          role="alert"
          className="w-full rounded-[12px] bg-[#FEE2E2] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#991B1B] md:mx-auto md:max-w-[440px]"
        >
          {error}
        </div>
      )}

      <div
        className="flex flex-col gap-4 md:mx-auto md:w-full md:max-w-[440px]"
        role="group"
        aria-label="Elegí uno o más tipos de contenido"
      >
        {SETUP_CONTENT_TYPES.map((c) => (
          <SelectableCard
            key={c.id}
            label={c.label}
            image={c.image}
            selected={selected.includes(c.id)}
            onClick={() => toggle(c.id)}
            role="checkbox"
            orientation="horizontal"
          />
        ))}
      </div>
    </SetupScaffold>
  );
};

export default AccountSetupContent;
